import { convertAddressFromDto } from "@/modules/address/addressDto"
import { convertImageFromDto } from "@/modules/dtos/imageDto"
import {
  type BoxesStaffMember,
  type HidableAttributeFloat,
  type HidableAttributeGenderCategory,
  type HidableAttributeString,
  type UserProfileDto,
} from "@/modules/users/gateway/usersDto"
import { type Address, type AddressDraft } from "@modules/address/addressTypes"
import { EmptyAddress } from "@modules/address/addressUtils"
import { type PhoneNumberWithPrefix } from "@modules/box/boxTypes"
import { type StripeAccountStatus } from "@modules/payment/stripeTypes"
import { type HUImage } from "@modules/types/imageTypes"
import dayjs from "dayjs"

export type UserProfileState = Omit<
  UserProfileEntity,
  "getSimpleProfileFields" | "getHideableFields" | "getAddressFields" | "getProfileCompletion" | "isOnlyMissingAvatar"
>

export class UserProfileEntity {
  id: string
  email: string
  firstname: string
  lastname: string
  username: string
  avatar: HUImage | null
  locale: string // todo
  inscriptionDate: Date
  notificationsEnabled: boolean
  stripeAccountStatus: StripeAccountStatus
  stripeAccountCurrency: string | null
  stripeMinimumAmount: number | null
  stripeMaximumAmount: number | null
  stripeAccountId: string | null
  biography: string | null
  emergencyInformation: string | null
  birthdate: HidableAttributeString | null
  crossfitStartDate: HidableAttributeString | null
  nationality: HidableAttributeString | null
  job: HidableAttributeString | null
  genderCategory: HidableAttributeGenderCategory | null
  weight: HidableAttributeFloat | null
  height: HidableAttributeFloat | null
  phoneNumber: PhoneNumberWithPrefix | null
  address: Address | null
  boxesStaffMember: BoxesStaffMember[]

  private constructor(user: UserProfileState) {
    this.id = user.id
    this.email = user.email
    this.firstname = user.firstname
    this.lastname = user.lastname
    this.username = user.username
    this.avatar = user.avatar
    this.locale = user.locale
    this.inscriptionDate = user.inscriptionDate
    this.notificationsEnabled = user.notificationsEnabled
    this.stripeAccountStatus = user.stripeAccountStatus
    this.stripeAccountCurrency = user.stripeAccountCurrency
    this.stripeMinimumAmount = user.stripeMinimumAmount
    this.stripeMaximumAmount = user.stripeMaximumAmount
    this.stripeAccountId = user.stripeAccountId
    this.biography = user.biography
    this.emergencyInformation = user.emergencyInformation
    this.birthdate = user.birthdate
    this.crossfitStartDate = user.crossfitStartDate
    this.nationality = user.nationality
    this.job = user.job
    this.genderCategory = user.genderCategory
    this.weight = user.weight
    this.height = user.height
    this.phoneNumber = user.phoneNumber
    this.address = user.address
    this.boxesStaffMember = user.boxesStaffMember
  }

  static fromDto(dto: UserProfileDto): UserProfileEntity {
    return new UserProfileEntity({
      id: dto.id,
      email: dto.email,
      firstname: dto.firstname,
      lastname: dto.lastname,
      username: dto.username,
      avatar: dto.avatar ? convertImageFromDto(dto.avatar) : null,
      locale: dto.locale,
      notificationsEnabled: dto.notificationsEnabled,
      stripeAccountStatus: dto.stripeAccountStatus,
      stripeAccountCurrency: dto.stripeAccountCurrency,
      stripeMinimumAmount: dto.stripeMinimumAmount,
      stripeMaximumAmount: dto.stripeMaximumAmount,
      stripeAccountId: dto.stripeAccountId,
      biography: dto.biography,
      emergencyInformation: dto.emergencyInformation,
      birthdate: dto.birthdate,
      crossfitStartDate: dto.crossfitStartDate,
      nationality: dto.nationality,
      job: dto.job,
      genderCategory: dto.genderCategory,
      weight: dto.weight,
      height: dto.height,
      phoneNumber: dto.phoneNumber,
      address: dto.address ? convertAddressFromDto(dto.address) : null,
      boxesStaffMember: dto.boxesStaffMember,
      inscriptionDate: dayjs(dto.inscriptionDate).toDate(),
    })
  }

  static getSimpleProfileFields(): (keyof UserProfileEntity)[] {
    return ["firstname", "lastname", "username", "avatar", "biography", "height", "weight", "emergencyInformation"]
  }

  static getHideableFields(): (keyof Pick<
    UserProfileEntity,
    "nationality" | "job" | "birthdate" | "crossfitStartDate" | "genderCategory"
  >)[] {
    return ["nationality", "job", "birthdate", "genderCategory", "crossfitStartDate"]
  }

  static getAddressFields(): (keyof Address)[] {
    return ["address", "city", "postalCode", "country"]
  }

  static getProfileCompletion(profile: Partial<UserProfileEntity>) {
    const simpleProperties = UserProfileEntity.getSimpleProfileFields()
    const hideableProperties = UserProfileEntity.getHideableFields()
    const addressProperties = UserProfileEntity.getAddressFields()

    // +1 for phoneNumber
    const totalProperties = simpleProperties.length + hideableProperties.length + addressProperties.length + 1
    let completedProperties = 0

    for (const prop of simpleProperties) {
      if (profile[prop]) completedProperties++
    }

    for (const prop of hideableProperties) {
      if (profile[prop]?.value) completedProperties++
    }

    for (const prop of addressProperties) {
      if (profile.address?.[prop]) completedProperties++
    }

    if (profile.phoneNumber?.number) completedProperties++

    const ratio = completedProperties / totalProperties
    return {
      pcRatio: ratio,
      current: completedProperties,
      total: totalProperties,
    }
  }

  static isOnlyMissingAvatar(profile: Partial<UserProfileEntity>): boolean {
    const completion = UserProfileEntity.getProfileCompletion(profile)
    const isMissingAvatar = !!profile.avatar === false
    const isOnlyMissingOneProfileProperty = completion.current === completion.total - 1

    if (isOnlyMissingOneProfileProperty && isMissingAvatar) return true
    return false
  }
}

export type UserProfileDraft = {
  firstname: string
  lastname: string
  username: string
  biography?: string
  imageKey?: string
  genderCategory?: HidableAttributeGenderCategory
  birthdate?: HidableAttributeString
  nationality?: HidableAttributeString
  notificationsEnabled?: boolean
  address: AddressDraft
  crossfitStartDate?: HidableAttributeString
  emergencyInformation?: string
  job?: HidableAttributeString
  weight?: HidableAttributeFloat
  height?: HidableAttributeFloat
  phoneNumber?: PhoneNumberWithPrefix
}

export function convertUserToDraft(user: UserProfileEntity | null): UserProfileDraft {
  return {
    ...user,
    firstname: user?.firstname ?? "",
    lastname: user?.lastname ?? "",
    username: user?.username ?? "",
    biography: user?.biography ?? "",
    imageKey: user?.avatar?.key ?? undefined,
    genderCategory: user?.genderCategory ?? undefined,
    birthdate: user?.birthdate ?? undefined,
    nationality: user?.nationality ?? undefined,
    notificationsEnabled: !!user?.notificationsEnabled,
    crossfitStartDate: user?.crossfitStartDate ?? undefined,
    emergencyInformation: user?.emergencyInformation ?? undefined,
    job: user?.job ?? undefined,
    weight: user?.weight ?? undefined,
    height: user?.height ?? undefined,
    phoneNumber: user?.phoneNumber ?? undefined,
    address: user?.address ?? EmptyAddress,
  }
}
